import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { endOfDay } from 'date-fns';

import SearchBar from '../../SearchBar';
import CustomButton from '../../CustomButton';
import ReportBreadcrumbBar from './ReportBreadcrumbBar';
import DateDropdown from '../../dump/menuComponents/DateDropdown';
import AddSubscriptionModal from './Modals/AddSubscriptionModal';
import AssetActivityMobileCard from './MobileReportCards/AssetActivityMobileCard';
import DataUsageMobileCard from './MobileReportCards/DataUsageMobileCard';
import FeatureUsageMobileCard from './MobileReportCards/FeatureUsageMobileCard';
import AssetEventBreakdownMobileCard from './MobileReportCards/AssetEventBreakdownMobileCard';
import GroupEventBreakdownMobileCard from './MobileReportCards/GroupEventBreakdownMobileCard';
import DriverEventBreakdownMobileCard from './MobileReportCards/DriverEventBreakdownMobileCard';
import DriverEventPerformanceMobileCard from './MobileReportCards/DriverEventPerformanceMobileCard';
import EventsMobileCard from './MobileReportCards/EventsMobileCard';
import IdlingSummaryMobileCard from './MobileReportCards/IdlingSummaryMobileCard';
import IdlingBreakdownMobileCard from './MobileReportCards/IdlingBreakdownMobileCard';
import PairedGoDeviceMobileCard from './MobileReportCards/PairedGoDeviceMobileCard';
import FirmwareMobileCard from './MobileReportCards/FirmwareMobileCard';
import ChannelStatusMobileCard from './MobileReportCards/ChannelStatusMobileCard';
import LocationHistoryMobileCard from './MobileReportCards/LocationHistoryMobileCard';
import MobileEventPagination from '../menuComponentsContainer/MobileEventPagination';
import { getDateRangeFromTabKey } from './ReportsHelpers';
import { getFleetAdminEventsSummary, getDevicesFeatureUsage } from '../../../action/FleetAdminActions';
import { getEventBreakdownReport, getIdlingSummaryReport, getIdlingBreakdownReport, getPairedGeotabDevices, setReportAddressList, getDriverEventBreakdownReport, getDriverEventPerformanceReport } from '../../../action/ReportsActions';
import { getEventsOfCompany } from '../../../action/AlertActions';
import { getCompanyById } from '../../../action/CompaniesActions';
import { getReportFirmware } from '../../../action/AccountActions';
import { getDevicesList, getCompanyDevicesList, getDeviceMetadata, getDeviceMetadataHistory } from '../../../action/DeviceActions';
import { downloadMobileCSV } from '../../helpers/mobileCSVExport';
import { formatVehicleName, formatGroup, formatTimestamp, formatFileSize, formatDataUsageCSV, formatOnlineStatusCSV, formatTimespanSeconds, formatChannelStatusCSV } from '../../helpers/tablesFuncHelpers';
import { exportIcon, filterIcon } from '../../helpers/iconHelpers';
import { CustomButtonToggle, CustomMenu } from '../../DropdownComponents';
import { Dropdown } from 'react-bootstrap';
import Autocomplete from 'react-autocomplete';
import { BsArrowDownUp } from 'react-icons/bs';
import _ from 'lodash';
import momenttz from '../../helpers/momenttz';
import '../../common/MobileGridCards.sass';

const MobileStandardReportBase = (props) => {
	const { goBack, selectedReport, mainTabs, breadcrumbs = [] } = props;
	const navigate = useNavigate();
	const { t, i18n } = useTranslation('translation', { keyPrefix: 'reportsPage.standardReportTable' });
	const { t: t2 } = useTranslation('translation', { keyPrefix: 'constants.tablesFuncHelpers' });
	const dispatch = useDispatch();
	const user = useSelector(state => state.user.user);
	const company = useSelector(state => state.company.company);
	const userRole = user.roles[0];
	const isProd = location.hostname === 'fleet.roscolive.com';
	const customDV7Access = isProd && [669, 1059, 1053, 3801, 2300].includes(company?.company_id);
	const zonesExitEntryReport = useSelector(state => state.reports.zonesExitEntryReport);
	const eventDetailEventTypes = [
		'fatalgsensor', 'highspeed', 'inputsensor', '%_clip', '%_alert', '%_event',
		'forwardcollisionwarning', 'lanedeparture', 'pedestrianwarning', 'headwaywarning',
		'drowsiness', 'distraction', 'phoneuse', 'yawning', 'seatbelt', 'smoking',
		'gsensor_wakeup', 'backup_warning', 'front_vehicle_start', 'trafficlightviolation_saw', 'trafficlightviolation_svw',
		'stopsignviolation_rsw', 'stopsignviolation_bsw', 'blind_spot_warning_standard', 'blind_spot_warning_urban',
		'low_speeding', 'moderate_speeding', 'high_speeding', 'severe_speeding',
	].filter((eventType) => !(customDV7Access && eventType === 'pedestrianwarning')).join(',');

	let initTabKey = 3;
	switch (selectedReport.defaultDateRange) {
		case 'Today':
			initTabKey = 1;
			break;
		case 'Yesterday':
			initTabKey = 2;
			break;
		case 'Last Week':
			initTabKey = 4;
			break;
		case 'This Month':
			initTabKey = 5;
			break;
		case 'Last Month':
			initTabKey = 6;
			break;
	}
	const dateRange = (zonesExitEntryReport && zonesExitEntryReport.reportId === selectedReport.id && zonesExitEntryReport.startDate && zonesExitEntryReport.endDate)
		? [zonesExitEntryReport.startDate, zonesExitEntryReport.endDate]
		: getDateRangeFromTabKey(initTabKey);

	const [reportSearch, setReportSearch] = useState('');
	const [reportData, setReportData] = useState([]);
	const [companyData, setCompanyData] = useState({});
	const [showAddSubscription, setShowAddSubscription] = useState(false);
	const [start_date, setStart_date] = useState(dateRange[0]);
	const [end_date, setEnd_date] = useState(dateRange[1]);
	const MOBILE_PAGE_SIZE = 10;
	const [mobilePage, setMobilePage] = useState(1);
	const [mobileSortOrder, setMobileSortOrder] = useState(selectedReport.defaultSortOrder || 'asc');
	const [mobileStatusFilter, setMobileStatusFilter] = useState('');
	const [devices, setDevices] = useState([]);
	const [selectedDevice, setSelectedDevice] = useState({});
	const [autoValue, setAutoValue] = useState('');

	const toggleLoader = (show) => dispatch({ type: 'TOGGLE_LOADER', payload: show });

	const nameFormat = (name) => i18n.t(`reportsPage.standardReportData.${name.replace(/\s/g, '_')}`);

	const getReportData = (start_date_arg = start_date, end_date_arg = end_date, company_name, partner_name) => {
		toggleLoader(true);

		switch (selectedReport.name) {
			case 'Asset Activity':
				getFleetAdminEventsSummary(userRole, company_name, partner_name)
					.then((res) => {
						setReportData(res.data.response);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'Data Usage': {
				const requestArray = [];
				requestArray.push(getCompanyById(userRole, company.company_id));
				requestArray.push(getDevicesList(company.company_id, 'inactive_devices,latest_hdderror,data_usage,ignition,sd_status,channel_status,driver,live_stream,no_sd_card,camera_health'));

				Promise.all(requestArray).then(
					(res) => {
						const companyDataRes = res[0].data.response.company;
						const devices = res[1].data.response.devices.sort((a, b) => a.data_usage > b.data_usage ? -1 : 1);
						setReportData(devices);
						setCompanyData(companyDataRes);
						toggleLoader(false);
					}
				)
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			}
			case 'Feature Usage':
				if (!(['custom_user', 'custom_partner'].includes(userRole) && !_.find(user.permissions, { perm_category_id: 7, a_view: true }))) {
					getDevicesFeatureUsage({ start_date: start_date_arg, end_date: end_date_arg }, userRole, company_name, partner_name)
						.then((res) => {
							setReportData(res.data.response.report);
							toggleLoader(false);
						})
						.catch(() => {
							toastr.error(t('toastrUnableGetData'));
							toggleLoader(false);
						});
				} else {
					toastr.error(t('toastrUnableGetData'));
					toggleLoader(false);
				}
				break;
			case 'Asset Event Breakdown':
				getEventBreakdownReport({
					company_id: company.company_id,
					start_date: momenttz(moment(`${start_date_arg}000000`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
					end_date: momenttz(moment(`${end_date_arg}235959`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
				})
					.then((res) => {
						const vehicleEvents = res.data.response.report.map((item) => {
							const { device_id, vehicle_name, vehicle_group, events } = item;
							return { device_id, vehicle_name, vehicle_group, ...events };
						});
						setReportData(vehicleEvents);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'Group Event Breakdown':
				getEventBreakdownReport({
					company_id: company.company_id,
					start_date: momenttz(moment(`${start_date_arg}000000`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
					end_date: momenttz(moment(`${end_date_arg}235959`, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')).utc().format('YYYYMMDDHHmmss'),
				})
					.then((res) => {
						const groupEvents = _(res.data.response.report)
							.groupBy('vehicle_group')
							.map((group, id) => {
								const events = { vehicle_group: id };
								group.forEach((event) => {
									Object.keys(event.events).forEach((key) => {
										events[key] = (events[key] ? events[key] : 0) + event.events[key];
									});
								});
								return events;
							})
							.value();
						setReportData(groupEvents);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'Driver Event Breakdown':
				getDriverEventBreakdownReport({
					company_id: company.company_id,
					start_date: momenttz(moment(`${start_date_arg}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
					end_date: momenttz(moment(`${end_date_arg}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
				})
					.then((res) => {
						const eventBreakdown = res.data.response.report.map((item) => {
							const obj = item['event_counts'];
							return { ...item, ...obj };
						});
						setReportData(eventBreakdown);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'Driver Event Performance':
				getDriverEventPerformanceReport({
					company_id: company.company_id,
					start_date: momenttz(moment(`${start_date_arg}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
					end_date: momenttz(moment(`${end_date_arg}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDD'),
				})
					.then((res) => {
						setReportData(res.data.response.report);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'DMS Events': {
				const typeFormatter = {
					'Drowsiness': 'Drowsiness/Head Down',
					'Distraction': 'Distraction',
					'Phone Use': 'Phone Use/Hand to Head',
					'Yawning': 'Yawning',
					'Seatbelt': 'Seatbelt',
					'Smoking': 'Smoking',
					'CameraBlock': 'Camera Blocked/Misaligned',
				};
				getEventsOfCompany({
					user_role: userRole,
					company_name: company_name,
					partner_name: partner_name,
					start_ts: moment(start_date_arg).utc().format('YYYYMMDDHHmmss'),
					end_ts: moment(end_date_arg).add(1, 'days').subtract(1, 'seconds').utc().format('YYYYMMDDHHmmss'),
					alert_types: '',
					event_types: 'phoneuse,drowsiness,yawning,distraction,seatbelt,smoking,camerablock',
					device_ids: 'all',
				})
					.then((res) => {
						const dmsReportData = [];
						res.data.response.forEach((device) => {
							device.events.forEach((event) => {
								dmsReportData.push({
									device_id: device.device_id,
									vehicle_name: device.vehicle_name,
									timestamp: event.timestamp,
									type: i18n.t(`constants.eventNames.${(typeFormatter[event.event_type] || event.event_type).replace(/\s/g, '_')}`, (typeFormatter[event.event_type] || event.event_type)),
									event_id: event.id,
								});
							});
						});
						setReportData(dmsReportData);
						toggleLoader(false);
					})
					.catch(() => {
						toggleLoader(false);
						toastr.error(t('toastrUnableGetData'));
					});
				break;
			}
			case 'Event Detail': {
				const typeFormatter = {
					'Drowsiness': 'Drowsiness/Head Down',
					'Distraction': 'Distraction',
					'Phone Use': 'Phone Use/Hand to Head',
					'Yawning': 'Yawning',
					'Seatbelt': 'Seatbelt',
					'Smoking': 'Smoking',
				};
				getEventsOfCompany({
					user_role: userRole,
					company_name: company_name,
					partner_name: partner_name,
					start_ts: moment(start_date_arg).utc().format('YYYYMMDDHHmmss'),
					end_ts: moment(end_date_arg).add(1, 'days').subtract(1, 'seconds').utc().format('YYYYMMDDHHmmss'),
					alert_types: '',
					event_types: eventDetailEventTypes,
					device_ids: 'all',
				})
					.then((res) => {
						const eventDetailReportData = [];
						res.data.response.forEach((device) => {
							device.events.forEach((event) => {
								eventDetailReportData.push({
									device_id: device.device_id,
									vehicle_name: device.vehicle_name,
									timestamp: event.timestamp,
									type: i18n.t(`constants.eventNames.${(typeFormatter[event.event_type] || event.event_type).replace(/\s/g, '_')}`, (typeFormatter[event.event_type] || event.event_type)),
									event_id: event.id,
								});
							});
						});
						setReportData(eventDetailReportData);
						toggleLoader(false);
					})
					.catch(() => {
						toggleLoader(false);
						toastr.error(t('toastrUnableGetData'));
					});
				break;
			}
			case 'Idling Summary':
				getIdlingSummaryReport(company.company_id, start_date_arg, end_date_arg)
					.then((res) => {
						setReportData(res.data.response.report);
						toggleLoader(false);
					})
					.catch(() => {
						toggleLoader(false);
						toastr.error(t('toastrUnableGetData'));
					});
				break;
			case 'Idling Breakdown':
				getIdlingBreakdownReport(company.company_id, start_date_arg, end_date_arg)
					.then((res) => {
						setReportData(res.data.response.report);
						toggleLoader(false);
					})
					.catch(() => {
						toggleLoader(false);
						toastr.error(t('toastrUnableGetData'));
					});
				break;
			case 'Paired GO Device Report':
				getPairedGeotabDevices({ company_id: company.company_id })
					.then((res) => {
						setReportData(res.data.response.devices);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetCompaniesData'));
						toggleLoader(false);
					});
				break;
			case 'Firmware':
				getReportFirmware(userRole, company_name, partner_name)
					.then((res) => {
						setReportData(res.data.response.devices);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'Channel Status':
				getDevicesList(company.company_id, 'ignition,channel_status')
					.then((res) => {
						const devices = res.data.response.devices.sort((a, b) => a.channel_status.length > b.channel_status.length ? -1 : 1);
						setReportData(devices);
						toggleLoader(false);
					})
					.catch(() => {
						toastr.error(t('toastrUnableGetData'));
						toggleLoader(false);
					});
				break;
			case 'Location History':
				if (!devices.length) {
					getCompanyDevicesList({ company_id: company.company_id })
						.then((res) => {
							toggleLoader(false);
							setDevices(res.data.response.devices);
						})
						.catch(() => {
							toastr.error(t('toastrUnableRetrieveVehicles'));
							setDevices([]);
							setReportData([]);
						});
				} else {
					toggleLoader(false);
				}
				// If a vehicle is already selected (e.g. date was changed), refresh the location data
				if (selectedDevice.device_id) {
					getLocationHistory(selectedDevice.device_id, start_date_arg);
				}
				break;
			default:
				setReportData([]);
				toggleLoader(false);
				break;
		}
	};

	const getLocationHistory = (device_id, start_date_arg) => {
		const start_ts = momenttz(moment(`${start_date_arg}`, 'YYYYMMDD').format('YYYY-MM-DD')).utc().format('YYYYMMDDHHmmss');
		const end_ts = moment(start_ts, 'YYYYMMDDHHmmss').add(24, 'h').format('YYYYMMDDHHmmss');
		toggleLoader(true);

		switch (userRole) {
			case 'system_admin':
			case 'customer_service':
			case 'partners':
				getDeviceMetadataHistory({ start_ts, end_ts, user_role: userRole, device_id })
					.then((res) => parseLocationHistory(res.data.response.metadata))
					.catch(() => {
						toastr.error(t('toastrGetDeviceLocationHistory'));
						toggleLoader(false);
					});
				break;
			default:
				getDeviceMetadata({ device_id, start_ts, end_ts })
					.then((res) => parseLocationHistory(res.data.response.metadata))
					.catch(() => {
						toastr.error(t('toastrGetDeviceLocationHistory'));
						toggleLoader(false);
					});
				break;
		}
	};

	const parseLocationHistory = (location_data) => {
		const report = [];
		const coords = [];
		const duration = moment.duration(2, 'minutes');
		let last_ts;

		for (let i = 0; i < location_data.length; i++) {
			if (report.length === 0 && location_data[i].type === 'Gps') {
				last_ts = location_data[i].timestamp;
				report.push({ time: location_data[i].timestamp });
				coords.push({ lat: location_data[i].latitude, lng: location_data[i].longitude });
			} else if (location_data[i].type === 'Gps') {
				if (moment(location_data[i].timestamp).subtract(duration) >= moment(last_ts)) {
					last_ts = location_data[i].timestamp;
					report.push({ time: location_data[i].timestamp });
					coords.push({ lat: location_data[i].latitude, lng: location_data[i].longitude });
				}
			}
		}

		if (report.length > 0 && coords.length <= 1000) {
			getAddress(coords.filter((e) => e.lat !== null && e.lng !== null), report);
		} else {
			toggleLoader(false);
			toastr.info(t('toastrNoHistory'));
			setReportData([]);
		}
	};

	const getAddress = (coords, report) => {
		const coords_list = coords.map((data) => JSON.stringify(data));
		toggleLoader(true);

		setReportAddressList({ coords_list })
			.then((res) => {
				const { response } = res.data;
				if (response.length) {
					report.forEach((data, i) => {
						if (response[i]) {
							const house_number = response[i].house_number || '';
							const road = response[i].road || '';
							const city = response[i].city ? `${response[i].city},` : '';
							const state = response[i].state || '';
							const postcode = response[i].postcode || '';
							data.address = house_number
								? `${house_number} ${road} - ${city} ${state} ${postcode}`
								: `${road} - ${city} ${state} ${postcode}`;
						} else {
							data.address = ' - ';
						}
					});
				} else {
					report.forEach((data) => { data.address = ' - '; });
				}
				toggleLoader(false);
				setReportData(report);
			})
			.catch(() => {
				report.forEach((data) => { data.address = ' - '; });
				toggleLoader(false);
				setReportData(report);
			});
	};

	const displayItemName = (item) => item.vehicle_name || item.device_id;
	const getItemValue = (item) => item.vehicle_name || item.device_id;
	const matchStateToTerm = (item, value) => {
		if (item.vehicle_name) {
			return item.vehicle_name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
		}
		return item.device_id.toString().toLowerCase().indexOf(value.toLowerCase()) !== -1;
	};

	const getReportParameters = (start_date_arg = start_date, end_date_arg = end_date) => {
		switch (userRole) {
			case 'fleet_maintainer':
			case 'fleet_manager':
			case 'group_manager':
			case 'user':
			case 'storage_manager':
			case 'storage_user':
			case 'custom_user':
				getReportData(start_date_arg, end_date_arg, null, null);
				break;
			case 'system_admin':
			case 'customer_service':
			case 'sales_demo':
				if (company.name && company.partner) {
					getReportData(start_date_arg, end_date_arg, company.name, company.partner);
				}
				break;
			case 'partner':
			case 'partner_view':
			case 'custom_partner':
				if (company.name) {
					getReportData(start_date_arg, end_date_arg, company.name, null);
				}
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		setReportData([]);
		setReportSearch('');
		getReportParameters();
	}, [company.company_id]);

	useEffect(() => {
		setMobilePage(1);
	}, [reportData, reportSearch, mobileStatusFilter]);

	const changeDate = (selected_start_date, selected_end_date) => {
		const new_start_date = selected_start_date ? moment(selected_start_date).format('YYYYMMDD') : start_date;
		const new_end_date = selected_end_date ? moment(selected_end_date).format('YYYYMMDD') : end_date;
		setStart_date(new_start_date);
		setEnd_date(new_end_date);
		getReportParameters(new_start_date, new_end_date);
	};

	const searchTerm = selectedReport.searchTerm || ['vehicle_name'];
	const filteredReportData = reportData && (reportSearch
		? ([7, 8].includes(+selectedReport.id)
			? reportData.filter((row) => Object.values(row).join(' ').toLowerCase().includes(reportSearch.toLowerCase()))
			: reportData.filter((row) => searchTerm.some((term) => row[term] && row[term].toString().toLowerCase().includes(reportSearch.toLowerCase()))))
		: reportData);

	const getDeviceStatusKey = (row) => (!row.online ? 'offline' : (row.ignition && row.ignition.state ? 'driving' : 'parked'));

	const availableStatuses = [
		{ key: 'driving', label: t2('online') },
		{ key: 'parked', label: t2('parked') },
		{ key: 'offline', label: t2('offline') },
	];
	const activeStatusLabel = availableStatuses.find((s) => s.key === mobileStatusFilter)?.label;

	const getStatusFilteredData = () => {
		switch (+selectedReport.id) {
			case 2: // Data Usage - filter by online/parked/offline status
			case 10: // Channel Status - filter by online/parked/offline status
				return mobileStatusFilter
					? filteredReportData.filter((row) => getDeviceStatusKey(row) === mobileStatusFilter)
					: filteredReportData;
			default:
				return filteredReportData;
		}
	};

	const statusFilteredData = getStatusFilteredData();

	const compareAssetNames = (nameA, nameB, order) => {
		const isEmptyA = !nameA || !nameA.trim();
		const isEmptyB = !nameB || !nameB.trim();
		if (isEmptyA !== isEmptyB) {
			return isEmptyA ? 1 : -1; // N/A always last, regardless of asc/desc
		}
		if (isEmptyA && isEmptyB) return 0;

		const isNumericA = /^\d/.test(nameA);
		const isNumericB = /^\d/.test(nameB);
		if (isNumericA !== isNumericB) {
			return isNumericA ? 1 : -1; // letters first, numbers last, regardless of asc/desc
		}
		if (nameA < nameB) return order === 'asc' ? -1 : 1;
		if (nameA > nameB) return order === 'asc' ? 1 : -1;
		return 0;
	};

	const getSortedReportData = () => {
		switch (+selectedReport.id) {
			case 1: // Asset Activity - sort by asset name
			case 2: // Data Usage - sort by asset name
			case 3: // Feature Usage - sort by asset name
			case 4: // Asset Event Breakdown - sort by asset name
			case 9: // Firmware - sort by asset name
			case 10: // Channel Status - sort by asset name
			case 14: // Idling Summary - sort by asset name
			case 15: { // Idling Breakdown - sort by asset name
				const sorted = [...statusFilteredData].sort((a, b) => {
					const nameA = formatVehicleName(a.device_id, a).toLowerCase();
					const nameB = formatVehicleName(b.device_id, b).toLowerCase();
					return compareAssetNames(nameA, nameB, mobileSortOrder);
				});
				return sorted;
			}
			case 5: { // Group Event Breakdown - sort by group name
				const sorted = [...statusFilteredData].sort((a, b) => {
					const nameA = (a.vehicle_group || '[Unnamed]').toLowerCase();
					const nameB = (b.vehicle_group || '[Unnamed]').toLowerCase();
					return compareAssetNames(nameA, nameB, mobileSortOrder);
				});
				return sorted;
			}
			case 7: // Driver Event Breakdown - sort by first name
			case 8: { // Driver Event Performance - sort by first name
				const sorted = [...statusFilteredData].sort((a, b) => {
					const nameA = (a.first_name || '').toLowerCase();
					const nameB = (b.first_name || '').toLowerCase();
					return compareAssetNames(nameA, nameB, mobileSortOrder);
				});
				return sorted;
			}
			case 6: // DMS Events - sort by timestamp, defaults to desc (most recent first)
			case 12: // Event Detail - sort by timestamp, defaults to desc (most recent first)
				return [...statusFilteredData].sort((a, b) => {
					const diff = moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf();
					return mobileSortOrder === 'asc' ? diff : -diff;
				});
			case 21: { // Paired GO Device Report - sort by camera name
				const sorted = [...statusFilteredData].sort((a, b) => {
					const nameA = (a.go_camera_name || a.dv_vehicle_name || '').toLowerCase();
					const nameB = (b.go_camera_name || b.dv_vehicle_name || '').toLowerCase();
					return compareAssetNames(nameA, nameB, mobileSortOrder);
				});
				return sorted;
			}
			case 18: // Location History - sort by time
				return [...statusFilteredData].sort((a, b) => {
					const diff = moment(a.time).valueOf() - moment(b.time).valueOf();
					return mobileSortOrder === 'asc' ? diff : -diff;
				});
			default:
				return statusFilteredData;
		}
	};

	const sortedReportData = getSortedReportData();

	const goBackFunc = () => {
		if (window.history.state.prevPath !== '/zones') {
			goBack();
		} else {
			navigate(-1);
		}
	};

	const editVehicle = (e, id) => {
		e.preventDefault();
		navigate('/managevehicles/edit', {
			state: { id, type: 'reports' }
		});
	};

	const renderMobileCards = () => {
		const pageData = sortedReportData.slice((mobilePage - 1) * MOBILE_PAGE_SIZE, mobilePage * MOBILE_PAGE_SIZE);
		switch (+selectedReport.id) {
			case 1: // Asset Activity
				return pageData.map((row, i) => (
					<AssetActivityMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} />
				));
			case 2: // Data Usage
				return pageData.map((row, i) => (
					<DataUsageMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} companyData={companyData} />
				));
			case 3: // Feature Usage
				return pageData.map((row, i) => (
					<FeatureUsageMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} />
				));
			case 4: // Asset Event Breakdown
				return pageData.map((row, i) => (
					<AssetEventBreakdownMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} columns={selectedReport.columns} />
				));
			case 5: // Group Event Breakdown
				return pageData.map((row, i) => (
					<GroupEventBreakdownMobileCard key={`${row.vehicle_group}_${i}`} row={row} columns={selectedReport.columns} />
				));
			case 7: // Driver Event Breakdown
				return pageData.map((row, i) => (
					<DriverEventBreakdownMobileCard key={`${row.first_name}_${row.last_name}_${i}`} row={row} columns={selectedReport.columns} />
				));
			case 8: // Driver Event Performance
				return pageData.map((row, i) => (
					<DriverEventPerformanceMobileCard key={`${row.first_name}_${row.last_name}_${i}`} row={row} />
				));
			case 6: // DMS Events
				return pageData.map((row, i) => (
					<EventsMobileCard key={row.event_id || i} row={row} editVehicle={editVehicle} />
				));
			case 12: // Event Detail - same columns/card as DMS Events
				return pageData.map((row, i) => (
					<EventsMobileCard key={row.event_id || i} row={row} editVehicle={editVehicle} />
				));
			case 14: // Idling Summary
				return pageData.map((row, i) => (
					<IdlingSummaryMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} />
				));
			case 15: // Idling Breakdown
				return pageData.map((row, i) => (
					<IdlingBreakdownMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} />
				));
			case 21: // Paired GO Device Report
				return pageData.map((row, i) => (
					<PairedGoDeviceMobileCard key={`${row.go_camera_name}_${i}`} row={row} />
				));
			case 9: // Firmware
				return pageData.map((row, i) => (
					<FirmwareMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} />
				));
			case 10: // Channel Status
				return pageData.map((row, i) => (
					<ChannelStatusMobileCard key={`${row.device_id}_${i}`} row={row} editVehicle={editVehicle} />
				));
			case 18: // Location History
				return pageData.map((row, i) => (
					<LocationHistoryMobileCard key={`${row.time}_${i}`} row={row} />
				));
			default:
				return null;
		}
	};

	const renderSortFilterRow = () => {
		switch (+selectedReport.id) {
			case 1: // Asset Activity - sort by name only, no status filter for this report
			case 3: // Feature Usage - sort by name only, no status filter for this report
			case 4: // Asset Event Breakdown - sort by name only, no status filter for this report
			case 9: // Firmware - sort by name only, no status filter for this report
			case 14: // Idling Summary - sort by name only, no status filter for this report
			case 15: // Idling Breakdown - sort by name only, no status filter for this report
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {t('labelAssetName')}
						</CustomButton>
					</div>
				);
			case 5: // Group Event Breakdown - sort by group name only, no status filter for this report
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {nameFormat('Group')}
						</CustomButton>
					</div>
				);
			case 7: // Driver Event Breakdown - sort by first name only, no status filter for this report
			case 8: // Driver Event Performance - sort by first name only, no status filter for this report
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {nameFormat('First Name')}
						</CustomButton>
					</div>
				);
			case 6: // DMS Events - sort by date/time only, no status filter for this report
			case 12: // Event Detail - sort by date/time only, no status filter for this report
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {nameFormat('Date/Time')}
						</CustomButton>
					</div>
				);
			case 21: // Paired GO Device Report - sort by camera name only, no status filter for this report
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {nameFormat('Camera Name')}
						</CustomButton>
					</div>
				);
			case 18: // Location History - sort by time only, no status filter for this report
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {nameFormat('Time')}
						</CustomButton>
					</div>
				);
			case 2: // Data Usage - sort by name + status filter
			case 10: // Channel Status - sort by name + status filter
				return (
					<div className="vehicle-table-sort-filter">
						<CustomButton
							variant="secondary-outline"
							prefix="sort-filter-button"
							onClick={() => setMobileSortOrder(mobileSortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<BsArrowDownUp size={14} />
							{t('textSort')}: {t('labelAssetName')}
						</CustomButton>
						<Dropdown>
							<Dropdown.Toggle as={CustomButtonToggle} variant="secondary-outline" prefix="sort-filter-button">
								{filterIcon}
								{activeStatusLabel ? `${nameFormat('Online Status')}: ${activeStatusLabel}` : nameFormat('Online Status')}
							</Dropdown.Toggle>
							<Dropdown.Menu as={CustomMenu}>
								<Dropdown.Item active={!mobileStatusFilter} onClick={() => setMobileStatusFilter('')}>
									{t2('all', { defaultValue: 'All' })}
								</Dropdown.Item>
								{availableStatuses.map((status) => (
									<Dropdown.Item
										key={status.key}
										active={mobileStatusFilter === status.key}
										onClick={() => setMobileStatusFilter(status.key)}
									>
										{status.label}
									</Dropdown.Item>
								))}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				);
			default:
				return null;
		}
	};

	const getCSVColumnDefs = () => {
		switch (+selectedReport.id) {
			case 1: // Asset Activity - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => formatGroup(row.groups, row) },
					{ header: nameFormat('Last Connected'), value: (row) => formatTimestamp(row.last_connected) },
					{ header: nameFormat('Last Event'), value: (row) => formatTimestamp(row.last_event) },
					{ header: nameFormat('Last Uploaded Event'), value: (row) => formatTimestamp(row.last_uploaded_event) },
					{ header: nameFormat('Event Count'), value: (row) => row.count },
					{ header: nameFormat('Total File Size'), value: (row) => formatFileSize(row.total_size) },
				];
			case 2: // Data Usage - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => formatGroup(row.groups, row) },
					{ header: nameFormat('Data Usage'), value: (row) => formatDataUsageCSV(null, row, companyData) },
					{ header: nameFormat('Device ID'), value: (row) => row.device_id },
					{ header: nameFormat('Online Status'), value: (row) => formatOnlineStatusCSV(row.ads_online, row) },
				];
			case 3: // Feature Usage - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => row.vehicle_group },
					{ header: nameFormat('Live Video (min)'), value: (row) => row.live_video_min },
					{ header: nameFormat('Event Clips'), value: (row) => row.event_clips_number },
					{ header: nameFormat('Custom Clips'), value: (row) => row.custom_clips_number },
				];
			case 4: // Asset Event Breakdown - CSV includes every column (incl. DV7-conditional ones not shown on the mobile card)
				return selectedReport.columns.map((column) => ({
					header: nameFormat(column.name),
					value: (row) => column.csvFormat ? column.csvFormat(row[column.dataField], row) : row[column.dataField],
				}));
			case 5: // Group Event Breakdown - CSV includes every column (incl. DV7-conditional ones not shown on the mobile card)
				return selectedReport.columns.map((column) => ({
					header: nameFormat(column.name),
					value: (row) => column.csvFormat ? column.csvFormat(row[column.dataField], row) : row[column.dataField],
				}));
			case 7: // Driver Event Breakdown - CSV includes every column (incl. DV7-conditional ones not shown on the mobile card)
				return selectedReport.columns.map((column) => ({
					header: nameFormat(column.name),
					value: (row) => column.csvFormat ? column.csvFormat(row[column.dataField], row) : row[column.dataField],
				}));
			case 8: // Driver Event Performance - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('First Name'), value: (row) => row.first_name },
					{ header: nameFormat('Last Name'), value: (row) => row.last_name },
					{ header: nameFormat('Group'), value: (row) => row.driver_group },
					{ header: nameFormat('Miles Driven/Event'), value: (row) => row.miles_driven_per_event },
					{ header: nameFormat('Miles Driven'), value: (row) => row.miles_driven },
				];
			case 6: // DMS Events - matches ReportsHelpers.js's standardReportData columns exactly
			case 12: // Event Detail - same columns as DMS Events
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Type'), value: (row) => row.type },
					{ header: nameFormat('Date/Time'), value: (row) => row.timestamp ? formatTimestamp(`${row.timestamp}+00`) : '-' },
					{ header: nameFormat('View Event'), value: (row) => row.event_id },
				];
			case 14: // Idling Summary - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => row.group_name },
					{ header: nameFormat('Idling Instances'), value: (row) => row.idling_instances },
					{ header: nameFormat('Total Time Idling'), value: (row) => formatTimespanSeconds(row.total_idling) },
					{ header: nameFormat('Average Idle Time'), value: (row) => formatTimespanSeconds(row.idling_average) },
				];
			case 15: // Idling Breakdown - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => row.group_name },
					{ header: nameFormat('Date/Time'), value: (row) => formatTimestamp(row.idling_ts) },
					{ header: nameFormat('Duration'), value: (row) => formatTimespanSeconds(row.idling_duration) },
				];
			case 21: // Paired GO Device Report - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Camera Name'), value: (row) => row.go_camera_name || row.dv_vehicle_name },
					{ header: nameFormat('Group (GO Device Group)'), value: (row) => row.go_device_groups },
					{ header: nameFormat('IMEI/SN'), value: (row) => row.rl_serial_number },
				];
			case 9: // Firmware - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => row.vehicle_group },
					{ header: nameFormat('Version'), value: (row) => row.firmware || '-' },
					{ header: nameFormat('Updated Date'), value: (row) => formatTimestamp(row.firmware_updated_ts) },
					{ header: nameFormat('Firmware Status'), value: (row) => row.firmware_status },
				];
			case 10: // Channel Status - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Asset'), value: (row) => formatVehicleName(row.device_id, row) },
					{ header: nameFormat('Group'), value: (row) => formatGroup(row.groups, row) },
					{ header: nameFormat('Channel Status'), value: (row) => formatChannelStatusCSV(row.channel_status, row) },
					{ header: nameFormat('Device ID'), value: (row) => row.device_id },
					{ header: nameFormat('Online Status'), value: (row) => formatOnlineStatusCSV(row.ads_online, row) },
				];
			case 18: // Location History - matches ReportsHelpers.js's standardReportData columns exactly
				return [
					{ header: nameFormat('Time'), value: (row) => formatTimestamp(row.time) },
					{ header: nameFormat('Address'), value: (row) => row.address },
				];
			default:
				return [];
		}
	};

	const formatCSVFileName = () => {
		const name = selectedReport.name.toLowerCase().replaceAll(' ', '_');
		const timeframe = moment(start_date).format('M-D-YY') + (start_date === end_date ? '' : '--' + moment(end_date).format('M-D-YY'));
		return `${name}${t('CSVFileName')}${timeframe}`;
	};

	const exportMobileReportCSV = () => {
		const columnDefs = getCSVColumnDefs();
		downloadMobileCSV(columnDefs, sortedReportData, formatCSVFileName());
	};

	const { dateEditable, defaultDateRange } = selectedReport;

	const reportDisplayName = nameFormat(selectedReport.reportName || selectedReport.name);

	return (
		<div className="report">
			<ReportBreadcrumbBar
				crumbs={[...breadcrumbs, nameFormat(selectedReport.name)]}
				onBack={goBackFunc}
				backLabel={t('btnBack')}
			/>
			{mainTabs}
			<div className="page-subheader">
				<div className="subheader-section search-flex report-search">
					<SearchBar term={reportSearch} placeholder={t('placeholderSearch')} onSearch={(e) => setReportSearch(e.target.value)} />
					{dateEditable && (
						selectedReport.name === 'Location History' ? (
							<DateDropdown
								start_date={start_date}
								end_date={end_date}
								onSelectDate={changeDate}
								singleDate={true}
								limitDates={['TODAY', 'YESTERDAY']}
								maxDate={endOfDay(new Date())}
							/>
						) : (
							<DateDropdown
								start_date={start_date}
								end_date={end_date}
								onSelectDate={changeDate}
								maxDate={endOfDay(new Date())}
							/>
						)
					)}
					{!dateEditable && defaultDateRange &&
						<CustomButton variant="primary" size="sm" disabled>{defaultDateRange}</CustomButton>
					}
				</div>
				{selectedReport.name !== 'Location History' && (
					<div className="subheader-section">
						<CustomButton variant="primary-outline" size="sm" onClick={() => setShowAddSubscription(true)}>{t('btnAutomateReport')}</CustomButton>
					</div>
				)}
			</div>

			<main className="main-content-block">
				<div className="table-header">
					<span className="report-title">{reportDisplayName}</span>
					<CustomButton variant="primary-outline" size="sm" prefix="export-button" onClick={exportMobileReportCSV}>
						{exportIcon}
						<span className="export-label">{t('titleExport')}</span>
					</CustomButton>
				</div>
				{selectedReport.name === 'Location History' && (
					<div className="autocomplete-wrapper">
						<span>{t('locationHistoryWrapper')}</span>
						<Autocomplete
							autoHighlight
							value={autoValue}
							inputProps={{ id: 'states-autocomplete-mobile' }}
							wrapperProps={{ className: 'search-autocomplete event-search-input' }}
							getItemValue={getItemValue}
							shouldItemRender={matchStateToTerm}
							menuStyle={{
								borderRadius: '3px',
								boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
								background: 'rgba(255, 255, 255, 0.9)',
								padding: '2px 0',
								fontSize: '90%',
								position: 'absolute',
								overflow: 'auto',
								maxHeight: '200px',
								right: '0',
								top: 'auto',
								zIndex: 100,
							}}
							onChange={(e, value) => {
								setAutoValue(value);
								setSelectedDevice(value);
							}}
							onSelect={(value, item) => {
								setAutoValue(value);
								setSelectedDevice(item);
								getLocationHistory(item.device_id, start_date);
							}}
							renderMenu={function (items, value, style) {
								return <div className="items-list" style={{ ...style, ...this.menuStyle }} children={items} />;
							}}
							renderItem={(item, isHighlighted) => (
								<div className={`item ${isHighlighted ? 'item-highlighted' : ''}`} key={item.device_id}>
									{displayItemName(item)}
								</div>
							)}
							renderInput={(inputProps) => (
								<input {...inputProps} placeholder={t('placeholderSearchVehicle')} className="form-control event-page-search" />
							)}
							items={devices}
						/>
					</div>
				)}
				{renderSortFilterRow()}
				<div className="mobile-report-cards-container">
					<div className="mobile-grid-cards-container">
						{renderMobileCards()}
					</div>
					<MobileEventPagination page={mobilePage} sizePerPage={MOBILE_PAGE_SIZE} totalSize={sortedReportData.length} onPageChange={(p) => setMobilePage(p)} />
				</div>
			</main>

			{showAddSubscription && (
				<AddSubscriptionModal
					onHide={() => setShowAddSubscription(false)}
					report={{ type: 'standard', report_id: selectedReport.id }}
				/>
			)}
		</div>
	);
};

export default MobileStandardReportBase;
